"use strict";
import JSBI from "jsbi";
import { UniverseChainId } from "features/chains/types";
import { DynamicConfigs, SwapConfigKey } from "features/gating/configs";
import { useDynamicConfigValue } from "features/gating/hooks";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { TransactionType } from "features/transactions/types/transactionDetails";
import { logger } from "utilities/src/logger/logger";
const NATIVE_CURRENCY_DECIMAL = 18;
const NATIVE_CURRENCY_DECIMAL_OFFSET = NATIVE_CURRENCY_DECIMAL - 4;
export function useMaxAmountSpend({
  currencyAmount,
  txType,
  isExtraTx = false
}) {
  const minAmountPerTx = useGetMinAmount(currencyAmount?.currency.chainId, txType);
  const multiplierAsPercent = useLowBalanceWarningGasPercentage();
  if (!currencyAmount || !minAmountPerTx) {
    return void 0;
  }
  const minAmount = JSBI.divide(
    JSBI.multiply(minAmountPerTx, JSBI.BigInt(isExtraTx ? multiplierAsPercent : 100)),
    JSBI.BigInt(100)
  );
  if (!currencyAmount.currency.isNative) {
    return currencyAmount;
  }
  const amount = JSBI.greaterThan(currencyAmount.quotient, minAmount) ? JSBI.subtract(currencyAmount.quotient, minAmount).toString() : "0";
  return getCurrencyAmount({
    value: amount,
    valueType: ValueType.Raw,
    currency: currencyAmount.currency
  });
}
function useGetMinAmount(chainId, txType) {
  const MIN_ETH_FOR_GAS = useMinEthForGas(txType);
  if (!chainId) {
    return void 0;
  }
  switch (chainId) {
    case UniverseChainId.Mainnet:
    case UniverseChainId.Sepolia:
      return MIN_ETH_FOR_GAS;
    default:
      logger.error(new Error("unhandled chain when getting min gas amount"), {
        tags: {
          file: "useMaxAmountSpend.ts",
          function: "getMinAmount"
        }
      });
      return MIN_ETH_FOR_GAS;
  }
}
export function useMinEthForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.EthSendMinGasAmount : SwapConfigKey.EthSwapMinGasAmount,
    isSend(txType) ? 150 : 20
    // .015 and .002 ETH
  );
}
export function useMinPolygonForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.PolygonSendMinGasAmount : SwapConfigKey.PolygonSwapMinGasAmount,
    isSend(txType) ? 600 : 75
    // .06 and .0075 MATIC
  );
}
export function useMinAvalancheForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.AvalancheSendMinGasAmount : SwapConfigKey.AvalancheSwapMinGasAmount,
    isSend(txType) ? 200 : 25
    // .02 and .0025 AVAX
  );
}
export function useMinCeloForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.CeloSendMinGasAmount : SwapConfigKey.CeloSwapMinGasAmount,
    isSend(txType) ? 100 : 13
    // .01 and .0013 CELO
  );
}
export function useMinMonForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.MonSendMinGasAmount : SwapConfigKey.MonSwapMinGasAmount,
    isSend(txType) ? 150 : 20
  );
}
export function useMinGenericL2ForGas(txType) {
  return useCalculateMinForGas(
    isSend(txType) ? SwapConfigKey.GenericL2SendMinGasAmount : SwapConfigKey.GenericL2SwapMinGasAmount,
    isSend(txType) ? 8 : 1
    // .0008 and .0001 ETH
  );
}
export function useLowBalanceWarningGasPercentage() {
  return useDynamicConfigValue(DynamicConfigs.Swap, SwapConfigKey.LowBalanceWarningGasPercentage, 100);
}
export function useCalculateMinForGas(amount, defaultAmount) {
  const multiplier = useDynamicConfigValue(DynamicConfigs.Swap, amount, defaultAmount);
  return JSBI.multiply(
    JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(NATIVE_CURRENCY_DECIMAL_OFFSET)),
    JSBI.BigInt(multiplier)
  );
}
function isSend(transactionType) {
  return transactionType === TransactionType.Send;
}
